/**
 * ABOVE-THE-FOLD TEASERS for logged-out visitors.
 *
 * Part 3 of `vault/how-to/seamless-page-auth-pattern.md`. A gate that says
 * only "Sign in to continue" converts worse than no gate at all: a stranger
 * arriving from a shared link has no idea what they are being asked to sign in
 * FOR, so they leave.
 *
 * That matters more for Shelv than for most modules, because **a shared link
 * IS the growth loop** — a forked recipe, an invite, one chef sending another
 * chef a URL. Every one of those people arrives logged out.
 *
 * ────────────────────────────────────────────────────────────────────────
 * ⚠️ WRITTEN, NEVER CLIPPED.
 * ────────────────────────────────────────────────────────────────────────
 *
 * These are prose written deliberately for strangers. The tempting
 * alternative — render the real page blurred, cropped or behind a fade — is
 * the DOM leak wearing a different hat: blur and `max-height` are CSS, and the
 * whole page would still be readable in view-source.
 *
 * **This file must never import a page component.** If it does, the leak has
 * been rebuilt. There is nothing here but strings.
 *
 * ── Scope (decided with Tom, 2026-09-07) ────────────────────────────────
 *
 * Not every route gets one, on purpose:
 *
 *   · the three SHAREABLE routes get a full teaser — they are the URLs a chef
 *     actually sends someone
 *   · the four PRO routes get a short descriptive one, saying what the page
 *     does and deliberately NOT naming a price (the founders have still not
 *     ratified one, and a stranger's first sight of Shelv is the worst place
 *     for an unagreed number)
 *   · kitchen, invite-a-friend and the two admin pages get the bare
 *     "Sign in to continue" fallback — nobody shares a link to an invite graph
 *
 * Register in `App.tsx`'s ROUTE_PREVIEWS, keyed by module id.
 */

export interface RoutePreview {
  title: string;
  blurb: string;
}

export const SHELV_PREVIEWS: Record<string, RoutePreview> = {
  // ── Shareable: what a chef actually sends someone ─────────────────────
  "/shelv/recipes": {
    title: "A recipe, on someone's shelf",
    blurb:
      "Rewritten into one house format, with the units a kitchen actually works in — readable at the pass on a wet phone. Sign in to view recipes you have access to. A shared invitation lets you accept your own copy.",
  },
  "/shelv/capture": {
    title: "Put a recipe on your shelf",
    blurb:
      "Paste it, or drop in a link. Shelv rewrites it into your house format with your units, and shows you the result before anything is saved — you fix what it got wrong, then keep it.",
  },
  "/shelv": {
    title: "The shelf",
    blurb:
      "Every recipe your kitchen cooks, in one format, in one place. Give a chef the whole book or just their section, and it stays the same on a Tuesday as it was the day you wrote it.",
  },

  // ── Pro: say what the page does. Never a price. ───────────────────────
  "/shelv/calendar": {
    title: "A week of service, planned",
    blurb:
      "Every dish across the week with the covers you expect, and every ingredient scaled to that number. Part of Shelv Pro.",
  },
  "/shelv/budget": {
    title: "What the week costs",
    blurb:
      "Predicted spend against what you actually spent, so the gap is still something you can do something about rather than something you find out too late. Part of Shelv Pro.",
  },
  "/shelv/orders": {
    title: "Ordering off the plan",
    blurb:
      "What the week's menu actually needs bought, grouped by supplier and shown to you before anything is generated. Part of Shelv Pro.",
  },
  "/shelv/feedback": {
    title: "The first plate",
    blurb:
      "The chef photographs the first plate of a service, and that photo becomes the standard. Anyone in the kitchen can say what came back. Part of Shelv Pro.",
  },
};

/** The module root. Prefix-matching from here would swallow every page. */
const MODULE_ROOT = "/shelv";

/**
 * Longest-prefix match, so a nested route falls back to its parent's teaser —
 * `/shelv/recipes/:id` resolves against the `/shelv/recipes` key, which is the
 * whole reason this is prefix-matched rather than an exact lookup: the
 * shareable URL a chef sends always carries an id.
 *
 * ⚠️ THE MODULE ROOT IS EXACT-MATCH ONLY.
 *
 * `/shelv` is a prefix of every other route in the module, so treating it like
 * the rest hands the shelf's teaser to `/shelv/invite-graph`, `/shelv/invites`
 * and `/shelv/kitchen` — pages that are deliberately meant to fall through to
 * the bare "Sign in to continue". Caught by `copyAndGate.test.tsx` on the
 * first run of the very test written to check the opposite property.
 *
 * Sorting longest-first still matters for the remaining keys.
 */
export const shelvPreviewForPath = (pathname: string): RoutePreview | undefined => {
  if (!pathname) return undefined;
  if (pathname === MODULE_ROOT) return SHELV_PREVIEWS[MODULE_ROOT];

  const keys = Object.keys(SHELV_PREVIEWS)
    .filter((k) => k !== MODULE_ROOT)
    .sort((a, b) => b.length - a.length);

  const hit = keys.find((k) => pathname === k || pathname.startsWith(`${k}/`));
  return hit ? SHELV_PREVIEWS[hit] : undefined;
};
